import React, { useEffect, useMemo, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";
import { motion } from "framer-motion";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);

const InventoryStatisticsChart = ({ data }) => {
  // Checkboxes (initially unchecked)
  const [filters, setFilters] = useState({ grn: false, dn: false, lo: false });

  // Determine display mode
  const isAnyChecked = useMemo(
    () => filters.grn || filters.dn || filters.lo,
    [filters]
  );

  // Default mode (none checked) => show all
  // Filter mode (some checked) => show only checked
  const showGRN = !isAnyChecked || filters.grn;
  const showDN = !isAnyChecked || filters.dn;
  const showLO = !isAnyChecked || filters.lo;

  // heading
  const selectedHeading = useMemo(() => {
    if (!isAnyChecked) return "Inventory Statistics";
    const selected = [];
    if (filters.grn) selected.push("Goods Received");
    if (filters.dn) selected.push("Delivery Notes");
    if (filters.lo) selected.push("Loading Orders");
    return `Showing: ${selected.join(" + ")}`;
  }, [filters, isAnyChecked]);

  // “DashLite-ish” styling config
  const filterMeta = useMemo(
    () => ({
      grn: {
        key: "grn",
        label: "Goods Received",
        color: "#6576ff",
        bg: "rgba(101, 118, 255, 0.10)",
      },
      dn: {
        key: "dn",
        label: "Delivery Notes",
        color: "#e01eb6",
        bg: "rgba(224, 30, 182, 0.10)",
      },
      lo: {
        key: "lo",
        label: "Loading Orders",
        color: "#139a3b",
        bg: "rgba(19, 154, 59, 0.10)",
      },
    }),
    []
  );

  // Default placeholder so chart isn't empty before API loads
  const [chartData, setChartData] = useState({
    labels: ["-", "-", "-", "-", "-", "-", "-"],
    datasets: [
      {
        label: "Goods Received",
        data: [0, 0, 0, 0, 0, 0, 0],
        borderColor: "#6576ff",
        backgroundColor: "rgba(101, 118, 255, 0.10)",
        pointBackgroundColor: "#6576ff",
        pointBorderColor: "#fff",
        pointHoverBackgroundColor: "#fff",
        pointHoverBorderColor: "#6576ff",
        fill: true,
        tension: 0.4,
      },
      {
        label: "Delivery Notes",
        data: [0, 0, 0, 0, 0, 0, 0],
        borderColor: "#e01eb6",
        backgroundColor: "rgba(224, 30, 182, 0.10)",
        pointBackgroundColor: "#e01eb6",
        pointBorderColor: "#fff",
        pointHoverBackgroundColor: "#fff",
        pointHoverBorderColor: "#e01eb6",
        fill: true,
        tension: 0.4,
      },
      {
        label: "Loading Orders",
        data: [0, 0, 0, 0, 0, 0, 0],
        borderColor: "#139a3b",
        backgroundColor: "rgba(19, 154, 59, 0.10)",
        pointBackgroundColor: "#139a3b",
        pointBorderColor: "#fff",
        pointHoverBackgroundColor: "#fff",
        pointHoverBorderColor: "#139a3b",
        fill: true,
        tension: 0.4,
      },
    ],
  });

  const normalizeSeries = (arr) => {
    if (!Array.isArray(arr)) return [];
    return arr
      .map((s) => {
        const date = (s?.date ?? "").toString().trim();
        const count = Number(s?.count ?? 0);
        if (!date) return null;
        return { date, count: Number.isFinite(count) ? count : 0 };
      })
      .filter(Boolean);
  };

  // Date helpers (expects "YYYY-MM-DD" from API)
  const parseISODateUTC = (iso) => {
    const [y, m, d] = (iso || "").split("-").map((x) => Number(x));
    if (!y || !m || !d) return null;
    return new Date(Date.UTC(y, m - 1, d));
  };

  const formatISODateUTC = (dateObj) => {
    const y = dateObj.getUTCFullYear();
    const m = String(dateObj.getUTCMonth() + 1).padStart(2, "0");
    const d = String(dateObj.getUTCDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  // Build continuous daily labels from minDate..maxDate
  const buildContinuousDateRange = (dates) => {
    const parsed = (dates || [])
      .map(parseISODateUTC)
      .filter((x) => x instanceof Date && !Number.isNaN(x.getTime()));

    if (parsed.length === 0) return [];

    const minTime = Math.min(...parsed.map((d) => d.getTime()));
    const maxTime = Math.max(...parsed.map((d) => d.getTime()));

    const labels = [];
    const oneDayMs = 24 * 60 * 60 * 1000;

    for (let t = minTime; t <= maxTime; t += oneDayMs) {
      labels.push(formatISODateUTC(new Date(t)));
    }
    return labels;
  };

  // Limit labels + values to last N dates
  const limitToLastN = (labels, seriesValues, max = 20) => {
    const start = Math.max(0, labels.length - max);
    return {
      labels: labels.slice(start),
      values: seriesValues.slice(start),
    };
  };

  useEffect(() => {
    if (!data) return;

    const grn = normalizeSeries(data?.grn_grouped_count);
    const dn = normalizeSeries(data?.dn_grouped_count);
    const lo = normalizeSeries(data?.loading_order_grouped_count);

    const allDates = [...grn, ...dn, ...lo].map((x) => x.date);

    // Build full continuous range (fills missing dates)
    const labelsAll = buildContinuousDateRange(allDates);
    if (labelsAll.length === 0) return;

    const mapByDate = (arr, labels) => {
      const m = {};
      arr.forEach((x) => (m[x.date] = Number(x.count) || 0));
      return labels.map((d) => m[d] || 0);
    };

    // Map values across full continuous range
    const grnValsAll = mapByDate(grn, labelsAll);
    const dnValsAll = mapByDate(dn, labelsAll);
    const loValsAll = mapByDate(lo, labelsAll);

    // Limit to last 20 dates (max dates on chart)
    const labels = limitToLastN(labelsAll, labelsAll, 20).labels;
    const grnVals = limitToLastN(labelsAll, grnValsAll, 20).values;
    const dnVals = limitToLastN(labelsAll, dnValsAll, 20).values;
    const loVals = limitToLastN(labelsAll, loValsAll, 20).values;

    const datasets = [];

    if (showGRN) {
      datasets.push({
        label: filterMeta.grn.label,
        data: grnVals,
        borderColor: filterMeta.grn.color,
        backgroundColor: filterMeta.grn.bg,
        pointBackgroundColor: filterMeta.grn.color,
        pointBorderColor: "#fff",
        pointHoverBackgroundColor: "#fff",
        pointHoverBorderColor: filterMeta.grn.color,
        fill: true,
        tension: 0.4,
      });
    }

    if (showDN) {
      datasets.push({
        label: filterMeta.dn.label,
        data: dnVals,
        borderColor: filterMeta.dn.color,
        backgroundColor: filterMeta.dn.bg,
        pointBackgroundColor: filterMeta.dn.color,
        pointBorderColor: "#fff",
        pointHoverBackgroundColor: "#fff",
        pointHoverBorderColor: filterMeta.dn.color,
        fill: true,
        tension: 0.4,
      });
    }

    if (showLO) {
      datasets.push({
        label: filterMeta.lo.label,
        data: loVals,
        borderColor: filterMeta.lo.color,
        backgroundColor: filterMeta.lo.bg,
        pointBackgroundColor: filterMeta.lo.color,
        pointBorderColor: "#fff",
        pointHoverBackgroundColor: "#fff",
        pointHoverBorderColor: filterMeta.lo.color,
        fill: true,
        tension: 0.4,
      });
    }

    if (datasets.length === 0) return;

    setChartData({ labels, datasets });
  }, [data, showGRN, showDN, showLO, filterMeta]);

  // UI pills
  const pillBase = {
    display: "inline-flex",
    alignItems: "center",
    gap: "10px",
    padding: "10px 12px",
    borderRadius: "999px",
    border: "1px solid #e5e9f2",
    background: "#fff",
    cursor: "pointer",
    userSelect: "none",
    transition: "all 160ms ease",
    fontSize: "13px",
    fontWeight: 600,
    color: "#364a63",
  };

  const hiddenCheckbox = {
    position: "absolute",
    opacity: 0,
    pointerEvents: "none",
    width: 0,
    height: 0,
  };

  const dotStyle = (color) => ({
    width: 10,
    height: 10,
    borderRadius: "50%",
    background: color,
    boxShadow: `0 0, 0, 4px ${color}22`,
    flexShrink: 0,
  });

  const TogglePill = ({ label, color, bg, checked, onChange }) => (
    <label
      style={{
        ...pillBase,
        borderColor: checked ? `${color}55` : "#e5e9f2",
        background: checked ? bg : "#fff",
      }}
    >
      <input
        style={hiddenCheckbox}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span style={dotStyle(color)} />
      <span style={{ lineHeight: 1 }}>{label}</span>
      <span
        style={{
          marginLeft: 6,
          width: 18,
          height: 18,
          borderRadius: "50%",
          border: checked ? `2px solid ${color}` : "2px solid #e5e9f2",
          background: checked ? color : "#fff",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          fontSize: 12,
          fontWeight: 900,
        }}
        aria-hidden="true"
      >
        {checked ? "✓" : ""}
      </span>
    </label>
  );

  const setAll = (checked) =>
    setFilters({ grn: checked, dn: checked, lo: checked });

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      style={{ width: "100%" }}
    >
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: "#364a63" }}>
          {selectedHeading}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "12px",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "14px",
        }}
      >
        <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
          <TogglePill
            label={filterMeta.grn.label}
            color={filterMeta.grn.color}
            bg={filterMeta.grn.bg}
            checked={filters.grn}
            onChange={(checked) => setFilters((p) => ({ ...p, grn: checked }))}
          />
          <TogglePill
            label={filterMeta.dn.label}
            color={filterMeta.dn.color}
            bg={filterMeta.dn.bg}
            checked={filters.dn}
            onChange={(checked) => setFilters((p) => ({ ...p, dn: checked }))}
          />
          <TogglePill
            label={filterMeta.lo.label}
            color={filterMeta.lo.color}
            bg={filterMeta.lo.bg}
            checked={filters.lo}
            onChange={(checked) => setFilters((p) => ({ ...p, lo: checked }))}
          />
        </div>

        <div style={{ display: "flex", gap: "10px" }}>
          <button
            type="button"
            onClick={() => setAll(true)}
            style={{
              border: "1px solid #e5e9f2",
              background: "#fff",
              padding: "9px 12px",
              borderRadius: "10px",
              cursor: "pointer",
              fontSize: "12px",
              fontWeight: 700,
              color: "#364a63",
            }}
          >
            Check All
          </button>
          <button
            type="button"
            onClick={() => setAll(false)}
            style={{
              border: "1px solid #e5e9f2",
              background: "#fff",
              padding: "9px 12px",
              borderRadius: "10px",
              cursor: "pointer",
              fontSize: "12px",
              fontWeight: 700,
              color: "#364a63",
            }}
          >
            Uncheck All
          </button>
        </div>
      </div>

      <div
        style={{
          height: "300px",
          width: "100%",
          overflow: "hidden",
          borderRadius: "14px",
          border: "1px solid #e5e9f2",
          background: "#fff",
          padding: "10px",
          boxSizing: "border-box",
        }}
      >
        <Line
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            layout: { padding: 0 },
            plugins: {
              legend: { display: false },
              tooltip: {
                enabled: true,
                backgroundColor: "rgba(17, 24, 39, 0.9)",
                titleColor: "#F9FAFB",
                bodyColor: "#F9FAFB",
                borderColor: "rgba(255, 255, 255, 0.1)",
                borderWidth: 1,
                titleFont: { size: 14 },
                bodyFont: { size: 13 },
                callbacks: {
                  label: (context) =>
                    context.dataset.label + ": " + context.parsed.y,
                },
              },
            },
            scales: {
              y: {
                beginAtZero: true,
                ticks: {
                  color: "#9eaecf",
                  font: { size: 12, weight: "bold" },
                  padding: 6,
                  stepSize: 5,
                },
                grid: { color: "rgba(82, 100, 132, 0.2)" },
                title: {
                  display: true,
                  text: "Count",
                  color: "#6B7280",
                  font: { size: 14, weight: "bold" },
                },
              },
              x: {
                ticks: {
                  color: "#9eaecf",
                  font: { size: 11, weight: "bold" },
                  padding: 6,
                  autoSkip: false,
                  minRotation: 45,
                  maxRotation: 45,
                },
                grid: { color: "transparent" },
                title: {
                  display: true,
                  text: "Date",
                  color: "#6B7280",
                  font: { size: 14, weight: "bold" },
                },
              },
            },
            interaction: { intersect: false, mode: "index" },
            animations: { tension: { duration: 1000, easing: "linear" } },
          }}
        />
      </div>
    </motion.div>
  );
};

export default InventoryStatisticsChart;
